import clsx from 'clsx';

import { type ChangeEvent, useId } from 'react';

import { useValue } from '../hooks/useValue';
import { Button } from '../buttons/Button';
import { FormControl, type FormControlProps } from './FormControl';
import './NumberField.css';

interface NumberFieldProps extends FormControlProps<number> {
  min?: number;
  max?: number;
}

const INPUT_STYLE = { width: '28px', height: '28px' };

export function useNumberField(props: NumberFieldProps) {
  const internalId = useId();

  const {
    id = internalId,
    name = id,
    value: initialValue = 0,
    onChange,
    min = 0,
    max = 10,
    disabled = false,
    dataTestId = 'NumberField',
    ...rest
  } = props;

  const { value, setValue } = useValue<number, string | number | boolean>({
    name: name!,
    defaultValue: initialValue,
    formatter: Number,
    onChange,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);

    if (newValue >= min && newValue <= max) {
      setValue(Number(newValue));
    }
  };

  const increase = () => {
    if (value < max) {
      setValue(value + 1);
    }
  };

  const decrease = () => {
    if (value > min) {
      setValue(value - 1);
    }
  };

  return {
    ...rest,
    id,
    min,
    max,
    name,
    value,
    dataTestId,
    disabled,
    handleChange,
    increase,
    decrease,
  };
}

export const NumberField = (props: NumberFieldProps) => {
  const {
    disabled,
    min,
    max,
    label,
    description,
    id,
    dataTestId,
    value,
    handleChange,
    increase,
    decrease,
    errorMessage,
    validationStatus,
    hideRequiredStar,
    required,
    ...rest
  } = useNumberField(props);

  return (
    <FormControl
      id={id}
      label={label}
      description={description}
      className={clsx('flex items-center justify-between', props.className)}
      dataName="NumberField"
      dataTestId={dataTestId}
      errorMessage={errorMessage}
      validationStatus={validationStatus}
      disabled={disabled}
      required={required}
      hideRequiredStar={hideRequiredStar}
      layout="vertical"
    >
      <div className="flex items-center gap-x-8">
        <Button
          onClick={decrease}
          variant="circle"
          disabled={value <= min || disabled}
          icon="MinusDefault"
          aria-label="Decrease"
          data-testid={`${dataTestId}-decrease`}
        />
        <input
          {...rest}
          className={clsx('text-b2 text-center font-normal outline-none', {
            'bg-pearl text-grey': disabled,
          })}
          style={INPUT_STYLE}
          type="number"
          id={id}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          value={value}
          min={min}
          max={max}
        />
        <Button
          onClick={increase}
          variant="circle"
          aria-label="Increase"
          disabled={value >= max || disabled}
          icon="PlusDefault"
          data-testid={`${dataTestId}-increase`}
        />
      </div>
    </FormControl>
  );
};
